import { useState, useEffect } from 'react';
import {
  Stack,
  Container,
  Group,
  Loader,
  Alert,
  Divider,
  ScrollArea,
  Anchor,
  Button,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import taskSchemaDefinition, { type Task } from '../../schemas/task.schema';
import { taskRepository, taskStatusRepository } from '@ayocore/tasks/client/rpc';
import { UserSelect } from '@ayocore/platform/identity/client/components/UserSelect';
import { TaskCommentModal } from '@ayocore/erp/crm/client/components/tasks/TaskCommentModal';
import { CompleteTaskModal } from '@ayocore/erp/crm/client/components/tasks/components/CompleteTaskModal';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import logger from '@ayocore/platform/common/helpers/logger';

/**
 * EditTaskForm - Task editing form with clean pattern
 */
export function EditTaskForm({
  taskId,
  onTaskUpdated,
}: {
  taskId: number;
  onTaskUpdated?: () => void;
}) {
  const [initialData, setInitialData] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const SchemaForm = useSchemaForm<Task>(taskSchemaDefinition.updateSchema, {
    hideDescriptions: true,
  });
  const taskTypeId = SchemaForm.values.task_type_id;
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const completeModal = useModal(CompleteTaskModal);

  const handleComplete = () => {
    if (!initialData) return;
    completeModal.onClose(() => onTaskUpdated?.());
    completeModal.open(
      {
        task: initialData,
        onCompleted: () => {
          completeModal.close();
          onTaskUpdated?.();
        },
        onCancel: () => completeModal.close(),
        title: `Завершить задачу «#${initialData.id} ${initialData.title}»`,
      },
      { size: 'md' }
    );
  };

  // Скрываем «Завершить» если задача уже в финальном статусе.
  const canComplete = !!initialData && !initialData.task_status?.is_final;

  // Модалка для комментария после обновления задачи
  const commentModal = useModal(TaskCommentModal);

  // Update form values when data loads
  useEffect(() => {
    if (initialData) {
      SchemaForm.setInitialData(initialData);
    }
  }, [initialData]);

  // When task type changes in edit form: if текущий статус не принадлежит новому типу — подобрать первый активный по sort_order
  useEffect(() => {
    const ensureValidStatusForType = async () => {
      if (!taskTypeId) return;
      try {
        setLoadingStatuses(true);
        const statuses = await taskStatusRepository
          .where('task_type_id', taskTypeId)
          .where('is_active', true)
          .orderBy('sort_order', 'asc')
          .fetch();
        const currentStatusId = SchemaForm.values.task_status_id;
        const existsInType = currentStatusId ? statuses.some(s => s.id === currentStatusId) : false;
        if (!existsInType) {
          const first = statuses[0];
          if (first) {
            SchemaForm.setFieldValue('task_status_id', first.id);
          }
        }
      } finally {
        setLoadingStatuses(false);
      }
    };
    ensureValidStatusForType();
  }, [taskTypeId]);

  // Load existing task data
  useEffect(() => {
    const loadTask = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const task = await taskRepository.findById(taskId);

        if (!task) {
          setLoadError(`Задача с ID ${taskId} не найдена`);
          return;
        }

        setInitialData(task);
      } catch (error) {
        logger.error('Failed to load task:', error);
        setLoadError(error instanceof Error ? error.message : 'Не удалось загрузить задачу');
      } finally {
        setLoading(false);
      }
    };

    if (taskId) {
      loadTask();
    }
  }, [taskId]);

  const handleSubmit = async (values: Task) => {
    // Update task using repository
    const task = await taskRepository.update(taskId, values);

    // Считаем, были ли значимые изменения (статус, дата в будущее)
    let statusChanged = false;
    let movedDateForward = false;
    let newDate: Date | null = null;

    if (initialData) {
      statusChanged = initialData.task_status_id !== task.task_status_id;

      const oldDate = initialData.due_date ? new Date(initialData.due_date) : null;
      const newDateObj = task.due_date ? new Date(task.due_date) : null;
      newDate = newDateObj;

      if (oldDate && newDateObj && newDateObj.getTime() > oldDate.getTime()) {
        movedDateForward = true;
      }
    }

    // Show success message
    SchemaForm.setSuccess(`✅ Задача "${task.title}" успешно обновлена!`);

    // Показываем модалку для комментария, если задача привязана к лиду или партнёру
    // и были значимые изменения: дата перенесена в будущее или изменен статус
    if (
      (task.entity_type === 'crm::LeadRepository' ||
        task.entity_type === 'crm::PartnerRepository') &&
      task.entity_uuid &&
      (statusChanged || movedDateForward)
    ) {
      // Закрытие крестиком/бекдропом — как «Пропустить»: отложенный onTaskUpdated
      commentModal.onClose(() => {
        setTimeout(() => onTaskUpdated?.(), 500);
      });
      commentModal.open(
        {
          title: 'Комментарий к изменению задачи',
          task,
          context: { statusChanged, movedDateForward, newDate },
          onClose: () => commentModal.close(),
          onCommentSaved: () => onTaskUpdated?.(),
        },
        { size: 'md' }
      );
    } else {
      // Если не привязана к лиду - просто закрываем форму
      setTimeout(() => {
        if (onTaskUpdated) {
          onTaskUpdated();
        }
      }, 1500);
    }

    return { success: true };
  };

  const applyDatePreset = (daysFromNow: number) => {
    const now = new Date();
    // UTC-полдень: локальная дата пользователя + daysFromNow, без day-drift при сохранении
    const d = new Date(
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + daysFromNow, 12, 0, 0, 0)
    );
    SchemaForm.setValues({ due_date: d });
  };

  const applyTimePreset = (hh: string, mm: string) => {
    SchemaForm.setValues({ due_time: `${hh}:${mm}:00` });
  };

  // Loading state
  if (loading) {
    return (
      <Container size='md' py='xl'>
        <Group justify='center'>
          <Loader size='md' />
          <span>Загрузка задачи...</span>
        </Group>
      </Container>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Container size='md' py='xl'>
        <Alert color='red' title='Ошибка'>
          {loadError}
        </Alert>
      </Container>
    );
  }

  // Don't render form until data is loaded
  if (!initialData) {
    return (
      <Container size='md' py='xl'>
        <Group justify='center'>
          <Loader size='md' />
          <span>Загрузка данных задачи...</span>
        </Group>
      </Container>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '80dvh' }}>
      {/* Scrollable Form Content */}
      <ScrollArea style={{ flex: 1 }} scrollHideDelay={0}>
        <form onSubmit={SchemaForm.handleSubmit(handleSubmit)}>
          <Stack gap='xs' pb='md'>
            <SchemaForm.Messages />

            {/* Basic Information */}
            <SchemaForm.Field.title />

            <SchemaForm.Field.description autosize minRows={4} maxRows={12} />

            <Divider />

            {/* Status and Assignment */}
            <Group grow>
              <SchemaForm.Field.task_status_id
                key={`edit-task-status-${taskTypeId ?? 'none'}`}
                repo={
                  taskTypeId
                    ? taskStatusRepository
                        .where('task_type_id', taskTypeId)
                        .where('is_active', true)
                        .orderBy('sort_order', 'asc')
                    : taskStatusRepository
                }
                displayField='name'
                placeholder={
                  taskTypeId ? 'Выберите статус задачи...' : 'Сначала выберите тип задачи'
                }
                disabled={!taskTypeId || loadingStatuses}
              />
              <UserSelect
                label='Ответственный'
                placeholder='Выберите исполнителя...'
                value={SchemaForm.values.assignee_id ?? null}
                onChange={id => SchemaForm.setFieldValue('assignee_id', id)}
              />
            </Group>

            <Divider />

            {/* Timing */}
            <Group grow>
              <Stack gap={4}>
                <SchemaForm.Field.due_date />
                <Group gap='xs'>
                  <Anchor size='xs' onClick={() => applyDatePreset(1)}>
                    Завтра
                  </Anchor>
                  <Anchor size='xs' onClick={() => applyDatePreset(2)}>
                    Послезавтра
                  </Anchor>
                  <Anchor size='xs' onClick={() => applyDatePreset(7)}>
                    Через неделю
                  </Anchor>
                </Group>
              </Stack>
              <Stack gap={4}>
                <SchemaForm.Field.due_time />
                <Group gap='xs'>
                  <Anchor size='xs' onClick={() => applyTimePreset('08', '00')}>
                    8:00
                  </Anchor>
                  <Anchor size='xs' onClick={() => applyTimePreset('10', '00')}>
                    10:00
                  </Anchor>
                  <Anchor size='xs' onClick={() => applyTimePreset('12', '00')}>
                    12:00
                  </Anchor>
                  <Anchor size='xs' onClick={() => applyTimePreset('14', '00')}>
                    14:00
                  </Anchor>
                  <Anchor size='xs' onClick={() => applyTimePreset('16', '00')}>
                    16:00
                  </Anchor>
                  <Anchor size='xs' onClick={() => applyTimePreset('18', '00')}>
                    18:00
                  </Anchor>
                </Group>
              </Stack>
            </Group>
          </Stack>

          {/* Sticky Footer with Action Buttons - INSIDE FORM */}
          <div
            style={{
              borderTop: '1px solid #e9ecef',
              backgroundColor: 'white',
              padding: '8px 0',
              flexShrink: 0,
              position: 'sticky',
              bottom: 0,
            }}
          >
            <Group justify='space-between'>
              <SchemaForm.buttons.cancel onClick={() => onTaskUpdated?.()} label='Отмена' />
              <Group gap='xs'>
                {canComplete && (
                  <Button
                    type='button'
                    color='green'
                    leftSection={<IconCheck size={12} />}
                    onClick={handleComplete}
                  >
                    Завершить
                  </Button>
                )}
                <SchemaForm.buttons.submit label='Сохранить' />
              </Group>
            </Group>
          </div>
        </form>
      </ScrollArea>
    </div>
  );
}

export default EditTaskForm;
